import { z } from 'zod';

export const IncTaxExTaxSchema = z.object({
  exTax: z.number(),
  incTax: z.number(),
});

export const TotalPricingSchema = z.object({
  exTax: z.number(),
  incTax: z.number(),
  tax: z.number(),
});

export const RetailPricingSchema = z.object({
  exTax: z.number(),
  taxable: z.boolean().optional(),
  taxCode: z.string().optional(),
});

export const PriceSchema = z.object({
  exTax: z.number(),
  incTax: z.number(),
  tax: z.number(),
  taxPercentage: z.number(),
});

export const OrderDiscountSchema = z.object({
  id: z.uuid().nullish(),
  code: z.string().nullish(),
  reason: z.string().nullish(),
  total: TotalPricingSchema,
});

export const PriceDisplaySchema = z.object({
  exTax: z.number(),
  incTax: z.number(),
  exTaxDisplay: z.string(),
  incTaxDisplay: z.string(),
});

export const OrderDiscountDisplaySchema = OrderDiscountSchema.omit({
  total: true,
}).extend({
  total: PriceDisplaySchema,
});

export const JobDiscountSchema = OrderDiscountSchema.omit({ id: true });

export const DiscountKind = z.enum(['None', 'Goodwill', 'PriceBeat']);

export const UnitSellDiscountSchema = z.object({
  id: z.uuid(),
  reason: z.string(),
  kind: DiscountKind,
  competitorName: z.string().optional(),
  competitorOfferedExTax: z.number().optional(),
  total: z.object({
    exTax: z.number(),
    incTax: z.number(),
    tax: z.number(),
  }),
});

export const UnitSellPricingSchema = z.object({
  quoted: PriceSchema,
  authorised: PriceSchema,
  discount: OrderDiscountSchema,
  discounts: z.array(UnitSellDiscountSchema).optional(),
});

export const UnitSellPricingDisplaySchema = z.object({
  quoted: PriceDisplaySchema,
  authorised: PriceDisplaySchema,
  discount: OrderDiscountDisplaySchema,
});

export const SellTotalSchema = z.object({
  labour: PriceSchema,
  quoted: PriceSchema,
  authorised: PriceSchema,
  discount: JobDiscountSchema,
});

export const SellTotalDisplaySchema = UnitSellPricingDisplaySchema.extend({
  labour: PriceDisplaySchema,
});

export const ExpenseTypeSchema = z.enum(['Tax', 'Fee']);

export const AdditionalExpenseSchema = z.object({
  authorityName: z.string(),
  type: ExpenseTypeSchema,
  amount: z.number(),
});

export const AdditionalExpenseGroupSchema = z.object({
  expenseType: ExpenseTypeSchema,
  totalAmount: z.number(),
  additionalExpenses: z.array(AdditionalExpenseSchema),
});

export const OrderTotalPriceDiscountSchema = z.object({
  id: z.uuid(),
  code: z.string().optional(),
  type: z.enum(['Value', 'Percentage']),
  jobId: z.uuid().optional(),
  jobRef: z.string().nullish(),
  total: TotalPricingSchema,
  quotedBefore: IncTaxExTaxSchema,
  authorisedBefore: IncTaxExTaxSchema,
  quotedAfter: IncTaxExTaxSchema,
  authorisedAfter: IncTaxExTaxSchema,
  dateApplied: z.coerce.date<Date>(),
  reason: z.string().nullish(),
  competitorName: z.string().optional(),
  competitorOfferedExTax: z.number().optional(),
  kind: DiscountKind,
});

export const OrderTotalPriceSchema = z.object({
  quoted: IncTaxExTaxSchema,
  authorised: IncTaxExTaxSchema,
  discount: z.number(),
});

export const OrderGrandTotalSchema = OrderTotalPriceSchema.extend({
  discounts: OrderTotalPriceDiscountSchema.array(),
});

export const OrderTotalPriceFeEnum = OrderGrandTotalSchema.keyof();

export const OrderTotalSchema = z.object({
  parts: OrderTotalPriceSchema,
  labour: OrderTotalPriceSchema,
  partsAndLabour: OrderTotalPriceSchema,
  shopSupplies: OrderTotalPriceSchema,
  fees: OrderTotalPriceSchema,
  federalExciseTax: OrderTotalPriceSchema,
  stateTyreFees: OrderTotalPriceSchema,
  warranty: OrderTotalPriceSchema.optional(),
  tax: OrderTotalPriceSchema,
  total: OrderGrandTotalSchema,
});

export type IncTaxExTaxModel = z.infer<typeof IncTaxExTaxSchema>;
export type PriceModel = z.infer<typeof PriceSchema>;
export type RetailPricingModel = z.infer<typeof RetailPricingSchema>;
export type TotalPricingModel = z.infer<typeof TotalPricingSchema>;
export type UnitSellPricingModel = z.infer<typeof UnitSellPricingSchema>;
export type OrderDiscountModel = z.infer<typeof OrderDiscountSchema>;
export type AdditionalExpenseModel = z.infer<typeof AdditionalExpenseSchema>;
export type OrderTotalPriceDiscountModel = z.infer<
  typeof OrderTotalPriceDiscountSchema
>;
export type OrderTotalModel = z.infer<typeof OrderTotalSchema>;
export type OrderTotalPriceModel = z.infer<typeof OrderTotalPriceSchema>;
export type OrderTotalPriceType = z.infer<typeof OrderTotalPriceFeEnum>;
export type UnitSellDiscountModel = z.infer<typeof UnitSellDiscountSchema>;
